import { Image, Text, View } from "@react-pdf/renderer";
import { Trans, useTranslation } from "react-i18next";
import { COMPRESSION_PILE, TENSION_PILE } from "../../../constants";
import Foot from "../../Math/Units/Imperial/Foot";
import Inch from "../../Math/Units/Imperial/Inch";
import Kilopound from "../../Math/Units/Imperial/Kilopound";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import Metre from "../../Math/Units/Metric/Metre";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import { DataRow, DataRowTextValue } from "../DataRow";
import { DataTableAASHTO } from "../DataTableAASHTO";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";

export function AASHTO_Aussere({ data }) {
  const { t } = useTranslation("translation");
  let itemIndex = 1;
  let isMetric = data.Aashto.aashtoAll.measurementType == 0;
  return (
    <>
      <PageWrapper Deckblatt={data.Deckblatt}>
        <View>
          {/* Outer Load Carrying Capacity*/}
          <Text style={styles.section}>{t("aashto.ausser.Outer Load Carrying Capacity")}</Text>

          {/* Information */}
          <Text style={styles.sectionSubText}>{t("aashto.ausser.Information")}</Text>
          {/* 
            Rs = π dbαdLb 
            Rs Nominal grout-to-ground bond resistance (kips) 
            db Diamter of micropile drill hole through bonded length (ft) 
            αb Nominal micropile grout-to-ground bond strength (ksf) 
            Lb Micropile bonded length (ft) 
            */}

          <View style={{ flexDirection: "row" }}>
            {/* R_s=\pi d_{b}\alpha_{d} L_{b} */}
            <Image
              src={{
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_s=\\pi\\cdot&space;b_d\\cdot&space;\\alpha_d\\cdot&space;L_b",
                method: "GET",
                headers: {},
              }}
              style={{
                height: 7,
                width: 69,
                marginLeft: 10,
              }}
            />
          </View>

          <View style={{ flexDirection: "row" }}>
            <View style={{ width: 20 }}>
              <Image
                src={{
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_s",
                  method: "GET",
                  headers: {},
                }}
                style={{
                  height: 7,
                  width: 9,
                  marginLeft: 10,
                  marginTop: 5,
                }}
              />
            </View>
            <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
              <Trans
                i18nKey="aashto.ausser.Nominal grout-to-ground bond resistance"
                values={{
                  unit: isMetric ? "kN" : "kip",
                }}
              />
            </Text>
          </View>

          <View style={{ flexDirection: "row" }}>
            <View style={{ width: 20 }}>
              <Image
                src={{
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}d_b",
                  method: "GET",
                  headers: {},
                }}
                style={{
                  height: 7,
                  width: 7,
                  marginLeft: 10,
                  marginTop: 5,
                }}
              />
            </View>
            <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
              <Trans
                i18nKey="aashto.ausser.Diamter of micropile drill hole through bonded length"
                values={{
                  unit: isMetric ? "m" : "ft",
                }}
              />
            </Text>
          </View>

          <View style={{ flexDirection: "row" }}>
            <View style={{ width: 20 }}>
              <Image
                src={{
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\alpha_b",
                  method: "GET",
                  headers: {},
                }}
                style={{
                  height: 5,
                  width: 8,
                  marginLeft: 10,
                  marginTop: 5,
                }}
              />
            </View>
            <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
              <Trans
                i18nKey="aashto.ausser.Nominal micropile grout-to-ground bond strength"
                values={{
                  unit: isMetric ? "kN/m²" : "ksf",
                }}
              />
            </Text>
          </View>

          <View style={{ flexDirection: "row" }}>
            <View style={{ width: 20 }}>
              <Image
                src={{
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_b",
                  method: "GET",
                  headers: {},
                }}
                style={{
                  height: 7,
                  width: 8,
                  marginLeft: 10,
                  marginTop: 5,
                }}
              />
            </View>
            <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
              <Trans
                i18nKey="aashto.ausser.Micropile bonded length"
                values={{
                  unit: isMetric ? "m" : "ft",
                }}
              />
            </Text>
          </View>

          {/* Outer Load Carrying Capacity */}
          <Text style={styles.sectionSubText}>
            {t("aashto.ausser.Outer Load Carrying Capacity")}
          </Text>

          <View style={tableStyles.tableContainer}>
            {/* Total Design Load */}
            <DataRow
              item={{
                title: t("aashto.inner.Total Design Load"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}P_{d}",
                  height: 7,
                  width: 9,
                },
                value: data.Aashto.aashtoAll.totalDesignLoad.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
              }}
            />

            {/* Selected Load Carrying Element */}
            <DataRowTextValue
              item={{
                title: t("aashto.inner.Selected Load Carrying Element"),
                value: data.Aashto.aashtoAll.selectedCarryingElement.name,
                itemIndex: itemIndex++,
              }}
            />
            {/* Belastungsart/-Richtung */}
            <DataRowTextValue
              item={{
                title: t("inner.loadType"),
                value: t(
                  data.Aashto.aashtoAll.loadType === COMPRESSION_PILE
                    ? "inner.compressionPile"
                    : "inner.tensionPile"
                ),
                itemIndex: itemIndex++,
              }}
            />
            {/* Pile inclination to the vertical: */}
            <DataRow
              item={{
                title: t("aashto.inner.Pile inclination to the vertical"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}\\beta",
                  height: 8,
                  width: 5,
                },
                value: data.Aashto.aashtoAll.pileInclinationFromVertical,
                itemIndex: itemIndex++,
                valueScale: 2,
                valueUnit: " °",
              }}
            />

            {/* Bit/Hole Size */}
            <DataRow
              item={{
                title: t("aashto.inner.Bit/Hole Size"),
                value: data.Aashto.aashtoAll.holeSize.current,
                itemIndex: itemIndex++,
                valueScale: 2,
                valueUnit: isMetric ? <Millimetre /> : <Inch />,
              }}
            />
          </View>

          {/* Soil Layers */}
          <Text style={styles.sectionSubText}>{t("aashto.ausser.Soil Layers")}</Text>

          {/* Tabelle */}
          <DataTableAASHTO
            item={data.Aashto.aashtoAll}
            tableInput={data.Aashto.aashtoAll.layersInput}
            tableCalculated={data.Aashto.aashtoAll.layersCalculated}
          />

          {/* Determining the total length */}
          <Text style={styles.sectionSubText}>
            {t("aashto.ausser.Determining the total length")}
          </Text>

          <View style={tableStyles.tableContainer}>
            {/* Length */}
            <DataRow
              item={{
                title: t("aashto.ausser.Length"),
                value: data.Aashto.aashtoAll.inclinedLengthTableSum.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Metre /> : <Foot />,
              }}
            />

            {/* Extension into foundation: */}
            <DataRow
              item={{
                title: t("aashto.ausser.Extension into foundation"),
                value: data.Aashto.aashtoAll.extensionIntoFoundation.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Metre /> : <Foot />,
              }}
            />

            {/* Total Length: */}
            <DataRow
              item={{
                title: t("aashto.ausser.Total Length"),
                value: data.Aashto.aashtoAll.totalLength.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Metre /> : <Foot />,
              }}
            />
          </View>
        </View>
      </PageWrapper>
    </>
  );
}
